import { useState } from "react"
import styled from "styled-components"

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 100%;
  min-height: 100vh;
`

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
`

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #555;
`

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`

const Checkbox = styled.input`
  margin: 0;
`

const Button = styled.button`
  background: #007bff;
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const FileUpload = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px dashed #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: #f8f9fa;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`

const PriorityBadge = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.5rem;
  
  ${(props) => {
    switch (props.priority) {
      case "urgent":
        return "background: #dc3545; color: white;"
      case "high":
        return "background: #fd7e14; color: white;"
      case "medium":
        return "background: #ffc107; color: #212529;"
      case "low":
        return "background: #28a745; color: white;"
      default:
        return "background: #6c757d; color: white;"
    }
  }}
`

const OrderForm = ({ apiEndpoints = {} }) => {
  const [formData, setFormData] = useState({
    customerName: "",
    email: "",
    phone: "",
    address: "",
    kitchenType: "",
    dimensions: {
      length: "",
      width: "",
      height: "",
    },
    cabinetStyle: "",
    material: "",
    countertopMaterial: "",
    appliances: [],
    budget: "",
    installationDate: "",
    specialRequirements: "",
    priority: "medium",
    projectTimeline: "",
    designPreferences: {
      colorScheme: "",
      lightingType: "",
      storageNeeds: "",
    },
    attachments: [],
    referralSource: "",
    previousKitchenAge: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name?.includes(".")) {
      const [parent, child] = name.split(".")
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      appliances: checked
        ? [...(prev.appliances || []), value]
        : (prev.appliances || []).filter((item) => item !== value),
    }))
  }

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || [])
    setFormData((prev) => ({
      ...prev,
      attachments: files,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const orderPayload = {
        customer: {
          name: formData.customerName || "",
          email: formData.email || "",
          phone: formData.phone || "",
          address: formData.address || "",
        },
        kitchen: {
          type: formData.kitchenType || "",
          dimensions: {
            length: Number.parseFloat(formData.dimensions?.length) || 0,
            width: Number.parseFloat(formData.dimensions?.width) || 0,
            height: Number.parseFloat(formData.dimensions?.height) || 0,
          },
          cabinetStyle: formData.cabinetStyle || "",
          material: formData.material || "",
          countertopMaterial: formData.countertopMaterial || "",
          appliances: formData.appliances || [],
        },
        project: {
          budget: formData.budget || "",
          installationDate: formData.installationDate || null,
          priority: formData.priority || "medium",
          timeline: formData.projectTimeline || "",
          specialRequirements: formData.specialRequirements || "",
        },
        design: {
          colorScheme: formData.designPreferences?.colorScheme || "",
          lightingType: formData.designPreferences?.lightingType || "",
          storageNeeds: formData.designPreferences?.storageNeeds || "",
        },
        metadata: {
          referralSource: formData.referralSource || "",
          previousKitchenAge: formData.previousKitchenAge || "",
          attachments: formData.attachments?.length || 0,
        },
        status: "pending",
        createdAt: new Date().toISOString(),
      }

      // Try API first
      let apiSuccess = false
      if (apiEndpoints.createOrder) {
        try {
          const response = await fetch(apiEndpoints.createOrder, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(orderPayload),
          })

          if (response?.ok) {
            const result = await response.json()
            console.log("Order created via API:", result)
            apiSuccess = true
            alert(`Order submitted successfully! Order ID: ${result?.data?.orderId || "N/A"}`)
          }
        } catch (apiError) {
          console.error("API submission failed:", apiError)
        }
      }

      // Always save to localStorage as backup
      const orders = JSON.parse(localStorage.getItem("kitchenOrders") || "[]")
      const newOrder = {
        id: Date.now(),
        ...orderPayload,
      }
      orders.push(newOrder)
      localStorage.setItem("kitchenOrders", JSON.stringify(orders))

      if (!apiSuccess) {
        alert("Order submitted successfully (saved locally)!")
      }

      // Reset form
      setFormData({
        customerName: "",
        email: "",
        phone: "",
        address: "",
        kitchenType: "",
        dimensions: { length: "", width: "", height: "" },
        cabinetStyle: "",
        material: "",
        countertopMaterial: "",
        appliances: [],
        budget: "",
        installationDate: "",
        specialRequirements: "",
        priority: "medium",
        projectTimeline: "",
        designPreferences: {
          colorScheme: "",
          lightingType: "",
          storageNeeds: "",
        },
        attachments: [],
        referralSource: "",
        previousKitchenAge: "",
      })
    } catch (error) {
      console.error("Submission error:", error)
      alert("Error submitting order. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormContainer>
      <Title>Kitchen Set Furniture Order Form</Title>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="customerName">Customer Name *</Label>
          <Input
            type="text"
            id="customerName"
            name="customerName"
            value={formData.customerName || ""}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="email">Email *</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone || ""}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label htmlFor="address">Installation Address *</Label>
          <TextArea id="address" name="address" value={formData.address || ""} onChange={handleInputChange} required />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="kitchenType">Kitchen Type *</Label>
            <Select
              id="kitchenType"
              name="kitchenType"
              value={formData.kitchenType || ""}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Kitchen Type</option>
              <option value="L-shaped">L-shaped</option>
              <option value="U-shaped">U-shaped</option>
              <option value="Galley">Galley</option>
              <option value="Island">Island</option>
              <option value="Peninsula">Peninsula</option>
              <option value="Straight">Straight</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="cabinetStyle">Cabinet Style *</Label>
            <Select
              id="cabinetStyle"
              name="cabinetStyle"
              value={formData.cabinetStyle || ""}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Cabinet Style</option>
              <option value="Modern">Modern</option>
              <option value="Traditional">Traditional</option>
              <option value="Shaker">Shaker</option>
              <option value="Flat Panel">Flat Panel</option>
              <option value="Raised Panel">Raised Panel</option>
            </Select>
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Kitchen Dimensions (in feet) *</Label>
          <Row>
            <div>
              <Label htmlFor="length">Length</Label>
              <Input
                type="number"
                id="length"
                name="dimensions.length"
                value={formData.dimensions?.length || ""}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="width">Width</Label>
              <Input
                type="number"
                id="width"
                name="dimensions.width"
                value={formData.dimensions?.width || ""}
                onChange={handleInputChange}
                required
              />
            </div>
          </Row>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="material">Cabinet Material *</Label>
            <Select id="material" name="material" value={formData.material || ""} onChange={handleInputChange} required>
              <option value="">Select Material</option>
              <option value="Solid Wood">Solid Wood</option>
              <option value="Plywood">Plywood</option>
              <option value="MDF">MDF</option>
              <option value="Particle Board">Particle Board</option>
              <option value="Laminate">Laminate</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="countertopMaterial">Countertop Material *</Label>
            <Select
              id="countertopMaterial"
              name="countertopMaterial"
              value={formData.countertopMaterial || ""}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Countertop</option>
              <option value="Granite">Granite</option>
              <option value="Quartz">Quartz</option>
              <option value="Marble">Marble</option>
              <option value="Laminate">Laminate</option>
              <option value="Butcher Block">Butcher Block</option>
            </Select>
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Appliances to Include</Label>
          <CheckboxGroup>
            {["Refrigerator", "Dishwasher", "Microwave", "Oven", "Cooktop", "Range Hood"].map((appliance) => (
              <CheckboxItem key={appliance}>
                <Checkbox
                  type="checkbox"
                  value={appliance}
                  checked={(formData.appliances || []).includes(appliance)}
                  onChange={handleCheckboxChange}
                />
                {appliance}
              </CheckboxItem>
            ))}
          </CheckboxGroup>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="budget">Budget Range *</Label>
            <Select id="budget" name="budget" value={formData.budget || ""} onChange={handleInputChange} required>
              <option value="">Select Budget Range</option>
              <option value="Under $10,000">Under $10,000</option>
              <option value="$10,000 - $25,000">$10,000 - $25,000</option>
              <option value="$25,000 - $50,000">$25,000 - $50,000</option>
              <option value="$50,000 - $100,000">$50,000 - $100,000</option>
              <option value="Over $100,000">Over $100,000</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="priority">Project Priority</Label>
            <Select id="priority" name="priority" value={formData.priority || "medium"} onChange={handleInputChange}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
            <PriorityBadge priority={formData.priority}>{(formData.priority || "medium").toUpperCase()}</PriorityBadge>
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="installationDate">Preferred Installation Date</Label>
            <Input
              type="date"
              id="installationDate"
              name="installationDate"
              value={formData.installationDate || ""}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="projectTimeline">Project Timeline</Label>
            <Select
              id="projectTimeline"
              name="projectTimeline"
              value={formData.projectTimeline || ""}
              onChange={handleInputChange}
            >
              <option value="">Select Timeline</option>
              <option value="1-2 weeks">1-2 weeks</option>
              <option value="3-4 weeks">3-4 weeks</option>
              <option value="1-2 months">1-2 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="Flexible">Flexible</option>
            </Select>
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Design Preferences</Label>
          <Row>
            <div>
              <Label htmlFor="colorScheme">Color Scheme</Label>
              <Select
                id="colorScheme"
                name="designPreferences.colorScheme"
                value={formData.designPreferences?.colorScheme || ""}
                onChange={handleInputChange}
              >
                <option value="">Select Color Scheme</option>
                <option value="Light & Neutral">Light & Neutral</option>
                <option value="Dark & Bold">Dark & Bold</option>
                <option value="Warm Tones">Warm Tones</option>
                <option value="Cool Tones">Cool Tones</option>
                <option value="Mixed">Mixed</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="lightingType">Lighting Preference</Label>
              <Select
                id="lightingType"
                name="designPreferences.lightingType"
                value={formData.designPreferences?.lightingType || ""}
                onChange={handleInputChange}
              >
                <option value="">Select Lighting</option>
                <option value="LED Under-cabinet">LED Under-cabinet</option>
                <option value="Pendant Lights">Pendant Lights</option>
                <option value="Recessed Lighting">Recessed Lighting</option>
                <option value="Natural Light Focus">Natural Light Focus</option>
                <option value="Mixed Lighting">Mixed Lighting</option>
              </Select>
            </div>
          </Row>
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="referralSource">How did you hear about us?</Label>
            <Select
              id="referralSource"
              name="referralSource"
              value={formData.referralSource || ""}
              onChange={handleInputChange}
            >
              <option value="">Select Source</option>
              <option value="Google Search">Google Search</option>
              <option value="Social Media">Social Media</option>
              <option value="Friend/Family">Friend/Family</option>
              <option value="Advertisement">Advertisement</option>
              <option value="Previous Customer">Previous Customer</option>
              <option value="Other">Other</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="previousKitchenAge">Current Kitchen Age</Label>
            <Select
              id="previousKitchenAge"
              name="previousKitchenAge"
              value={formData.previousKitchenAge || ""}
              onChange={handleInputChange}
            >
              <option value="">Select Age</option>
              <option value="Less than 5 years">Less than 5 years</option>
              <option value="5-10 years">5-10 years</option>
              <option value="10-20 years">10-20 years</option>
              <option value="20+ years">20+ years</option>
              <option value="New Construction">New Construction</option>
            </Select>
          </FormGroup>
        </Row>

        <FormGroup>
          <Label htmlFor="attachments">Upload Images/Plans (Optional)</Label>
          <FileUpload type="file" id="attachments" multiple accept="image/*,.pdf,.dwg" onChange={handleFileUpload} />
          {formData.attachments?.length > 0 && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#666" }}>
              {formData.attachments.length} file(s) selected
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="specialRequirements">Special Requirements</Label>
          <TextArea
            id="specialRequirements"
            name="specialRequirements"
            value={formData.specialRequirements || ""}
            onChange={handleInputChange}
            placeholder="Any special requirements, accessibility needs, or additional notes..."
          />
        </FormGroup>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit Order"}
        </Button>
      </form>
    </FormContainer>
  )
}

export default OrderForm
